import { ArrowRight } from "lucide-react";
import { Reveal, Label } from "./Reveal";
import { CALENDLY_URL } from "../lib/contact";
import { trackEvent } from "../lib/tracking";

// Compact quick-start sequence, immediately after the hero/trust strip:
// a first-time (often paid-search) visitor should grasp the whole process in
// seconds, before exploring age-specific services. The deeper process section
// ("What happens after we talk?") lives further down the page. Generous bottom
// padding guarantees the floating concierge bar can never cover the steps.
const STEPS = [
  {
    n: "01",
    title: "Reserve your complimentary pediatric sleep assessment",
    body: "Choose a time that works for you and tell Andrea a little about what's happening with sleep. Andrea personally sets aside this time for your family.",
    value: true,
  },
  {
    n: "02",
    title: "Understand what's getting in the way",
    body: "Andrea will learn about your child, your routines, what you've tried, what's been difficult, and what you want sleep to look like. Together, you'll get clearer on what's happening and what Andrea recommends.",
  },
  {
    n: "03",
    title: "Choose your next step",
    body: "If professional support makes sense, Andrea will walk you through the approach she recommends, what's included, and what working together would look like. If you choose to continue, she'll build your personalized plan and support you through implementation.",
  },
];

export const HowItWorks = () => (
  <section id="start-today" data-testid="how-it-works-section" className="bg-ivory border-t border-ink/10">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-16 lg:pt-24 pb-36 lg:pb-48">
      <Reveal>
        <Label>How it works</Label>
      </Reveal>
      <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
        <Reveal as="h2" delay={0.06} className="font-serif font-light text-ink text-4xl sm:text-5xl leading-[1.08] tracking-tight">
          Getting better sleep
          <span className="block">can start <span className="italic text-clay">today.</span></span>
        </Reveal>
        <Reveal delay={0.1}>
          <p data-testid="how-it-works-sub" className="max-w-md text-base sm:text-lg font-normal leading-relaxed text-smoke">
            No complicated process. Start with a complimentary pediatric sleep
            assessment, understand what&rsquo;s happening, and decide what kind
            of support feels right for your family.
          </p>
        </Reveal>
      </div>

      <div className="mt-12 lg:mt-16 grid gap-10 md:grid-cols-3 md:gap-8">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={0.08 * i}>
            <div data-testid={`how-it-works-step-${i + 1}`} className="pt-6 border-t border-ink/10">
              <p aria-hidden="true" className="font-serif font-light text-4xl lg:text-5xl text-clay/70 leading-none">
                {s.n}
              </p>
              <h3 className="mt-5 font-serif font-light text-ink text-2xl leading-snug tracking-tight">
                {s.title}
              </h3>
              <p className="mt-3 text-base font-normal leading-relaxed text-smoke">
                {s.body}
              </p>
              {s.value && (
                <p
                  data-testid="how-it-works-value"
                  className="mt-6 font-serif font-light text-xl sm:text-2xl leading-snug text-ink"
                >
                  A <span className="italic text-clay">$149</span> professional
                  assessment —{" "}
                  <span className="italic text-clay">complimentary</span>
                </p>
              )}
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <a
          data-testid="how-it-works-cta"
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent("cta_click", { location: "how_it_works" });
            trackEvent("book_assessment_click", { location: "how_it_works" });
          }}
          className="mt-12 w-full sm:w-auto justify-center inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-8 py-4 text-base hover:bg-espresso transition-colors duration-300 group"
        >
          Book my complimentary pediatric sleep assessment
          <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </Reveal>
    </div>
  </section>
);
